// Polls navigator.getGamepads() once per animation frame and emits
// button-down / button-up / axis events on a tiny EventTarget. Runs
// only while there is an active subscriber AND a connected pad. Idles
// to zero overhead otherwise.
//
// Also forwards events from the Web HID bridge (hidGamepadBus). On
// macOS Chromium's Gamepad API does not reliably see wired Xbox
// controllers; when the user grants Web HID access via the settings
// panel, hidGamepadBridge parses raw HID reports and publishes events
// on hidGamepadBus. We re-emit those onto every gamepadPoll instance
// so downstream consumers (focus bridge, GameBoard, VirtualKeyboard,
// rebind UI) don't care which backend produced the event.
//
// The Gamepad API in Chromium does not deliver button events on its
// own; you must diff successive polls. This module owns that diff plus
// a handful of ergonomic affordances:
//
//   - Dead-zone clamping for sticks (below DEAD_ZONE → treated as 0).
//   - Held-button repeat for the d-pad (first repeat after 400 ms,
//     subsequent every 100 ms). Matches typical OS key-repeat cadence.
//   - Visibility / blur safe: when the document hides we reset all
//     held state so returning to focus never fires phantom up/down
//     pairs for buttons that were released while we weren't looking.

import { hidGamepadBus } from './hidGamepadBridge';

export const DEAD_ZONE = 0.18;
export const REPEAT_INITIAL_MS = 400;
export const REPEAT_INTERVAL_MS = 100;

// Standard-mapping indices we care about for repeat. Xbox: d-pad is
// 12/13/14/15, LB/RB 4/5, A/B/X/Y 0/1/2/3.
const DPAD_BUTTONS = new Set([12, 13, 14, 15]);

function makeEmitter() {
  const target = new EventTarget();
  return {
    on(name, fn) {
      target.addEventListener(name, fn);
      return () => target.removeEventListener(name, fn);
    },
    emit(name, detail) {
      target.dispatchEvent(new CustomEvent(name, { detail }));
    },
  };
}

function readGamepad(index) {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
  const pads = navigator.getGamepads();
  if (!pads) return null;
  return pads[index] || null;
}

// Produce a shallow snapshot. We only care about .pressed for buttons
// (value/analog is a Phase 4 concern) and about the four axes defined
// by the standard mapping. If a pad reports fewer axes we pad with 0.
function snapshot(pad) {
  if (!pad) return null;
  const buttons = new Array(pad.buttons.length);
  for (let i = 0; i < pad.buttons.length; i++) buttons[i] = Boolean(pad.buttons[i].pressed);
  const axes = [0, 0, 0, 0];
  for (let i = 0; i < Math.min(4, pad.axes.length); i++) {
    const v = pad.axes[i];
    axes[i] = Math.abs(v) < DEAD_ZONE ? 0 : v;
  }
  return { id: pad.id, index: pad.index, buttons, axes, timestamp: pad.timestamp };
}

export function createGamepadPoll({
  padIndex = 0,
  getGamepad = () => readGamepad(padIndex),
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  raf = (typeof requestAnimationFrame === 'function')
    ? ((cb) => requestAnimationFrame(cb))
    : ((cb) => setTimeout(() => cb(now()), 16)),
  caf = (typeof cancelAnimationFrame === 'function')
    ? ((id) => cancelAnimationFrame(id))
    : ((id) => clearTimeout(id)),
} = {}) {
  const { on, emit } = makeEmitter();
  let prev = null;
  let running = false;
  let rafId = null;
  // For each held d-pad button, { pressedAt, lastFireAt }.
  const repeatState = new Map();
  // Forwarding handlers for the HID bus. Kept on an object so start()
  // can attach them and stop() can detach by identity.
  const hidForward = { onDown: null, onUp: null, onAxis: null };

  function resetHeldState() {
    prev = null;
    repeatState.clear();
  }

  function tick() {
    if (!running) return;
    const pad = getGamepad();
    const snap = snapshot(pad);

    // Pad went away (or never existed). Emit release events for any
    // buttons we thought were held, then idle this frame.
    if (!snap) {
      if (prev) {
        for (let i = 0; i < prev.buttons.length; i++) {
          if (prev.buttons[i]) emit('button-up', { index: i });
        }
      }
      prev = null;
      repeatState.clear();
      rafId = raf(tick);
      return;
    }

    const t = now();

    // Button diff.
    if (prev) {
      const len = Math.max(prev.buttons.length, snap.buttons.length);
      for (let i = 0; i < len; i++) {
        const before = Boolean(prev.buttons[i]);
        const after = Boolean(snap.buttons[i]);
        if (!before && after) {
          emit('button-down', { index: i });
          if (DPAD_BUTTONS.has(i)) repeatState.set(i, { pressedAt: t, lastFireAt: t });
        } else if (before && !after) {
          emit('button-up', { index: i });
          repeatState.delete(i);
        }
      }
    } else {
      // First frame: fire a down for anything already held so the
      // consumer starts in a coherent state. Rare, but harmless.
      for (let i = 0; i < snap.buttons.length; i++) {
        if (snap.buttons[i]) {
          emit('button-down', { index: i });
          if (DPAD_BUTTONS.has(i)) repeatState.set(i, { pressedAt: t, lastFireAt: t });
        }
      }
    }

    // Axis events. Only emit on change beyond a small epsilon. Keeps
    // consumer re-renders cheap when the stick is idle in the dead
    // zone (both values snap to exact zero there).
    if (!prev || axesDiffer(prev.axes, snap.axes)) {
      emit('axis', { axes: snap.axes.slice() });
    }

    // D-pad repeat.
    for (const [idx, state] of repeatState) {
      if (!snap.buttons[idx]) {
        repeatState.delete(idx);
        continue;
      }
      const held = t - state.pressedAt;
      if (held < REPEAT_INITIAL_MS) continue;
      if (t - state.lastFireAt >= REPEAT_INTERVAL_MS) {
        emit('button-down', { index: idx, repeat: true });
        state.lastFireAt = t;
      }
    }

    prev = snap;
    rafId = raf(tick);
  }

  function start() {
    if (running) return;
    running = true;
    prev = null;
    repeatState.clear();
    rafId = raf(tick);

    // Subscribe to the Web HID bus so events from the HID backend
    // reach this instance's listeners too. Declared as plain
    // functions so they can be removed on stop().
    hidForward.onDown = (e) => emit('button-down', e.detail);
    hidForward.onUp   = (e) => emit('button-up',   e.detail);
    hidForward.onAxis = (e) => emit('axis',        e.detail);
    hidGamepadBus.addEventListener('button-down', hidForward.onDown);
    hidGamepadBus.addEventListener('button-up',   hidForward.onUp);
    hidGamepadBus.addEventListener('axis',        hidForward.onAxis);
  }

  function stop() {
    running = false;
    if (rafId !== null) caf(rafId);
    rafId = null;
    resetHeldState();

    if (hidForward.onDown) hidGamepadBus.removeEventListener('button-down', hidForward.onDown);
    if (hidForward.onUp)   hidGamepadBus.removeEventListener('button-up',   hidForward.onUp);
    if (hidForward.onAxis) hidGamepadBus.removeEventListener('axis',        hidForward.onAxis);
    hidForward.onDown = hidForward.onUp = hidForward.onAxis = null;
  }

  // Visibility hook. Exposed so callers can wire it to
  // document.visibilitychange. We don't attach it ourselves so the
  // module stays friendly to non-DOM test environments.
  function onVisibilityChange(hidden) {
    if (hidden) {
      // Emit up events for anything we believed was held, so consumers
      // don't get stuck in a "button-is-held" state.
      if (prev) {
        for (let i = 0; i < prev.buttons.length; i++) {
          if (prev.buttons[i]) emit('button-up', { index: i });
        }
      }
      resetHeldState();
    }
  }

  return { start, stop, on, onVisibilityChange };
}

function axesDiffer(a, b) {
  for (let i = 0; i < 4; i++) if (a[i] !== b[i]) return true;
  return false;
}
